import { useEffect, useState } from "react";

/**
 * The one breakpoint DESIGN.md § Layout & Spacing names: `<768px` is phone,
 * `≥768px` is desktop — Tailwind's own `md:` prefix, mirrored here as a JS
 * value for the places rendering itself has to branch (page size 50/25,
 * which columns compose the phone two-line row), not just styling. A media
 * query string has no way to reference a CSS theme token, so the boundary
 * is named as a constant here instead of an inline magic number — one
 * pixel below Tailwind's `md` breakpoint, matching `max-width`'s inclusive
 * upper bound to DESIGN.md's own `< 768px` wording.
 */
const DESKTOP_BREAKPOINT_PX = 768;
const PHONE_MEDIA_QUERY = `(max-width: ${DESKTOP_BREAKPOINT_PX - 1}px)`;

function readMatches(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => readMatches(query));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQueryList = window.matchMedia(query);
    const listener = () => setMatches(mediaQueryList.matches);
    listener();
    mediaQueryList.addEventListener("change", listener);
    return () => mediaQueryList.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_MEDIA_QUERY);
}
